import { useEffect, useRef, useState } from "react"
import {
  createMeal,
  parseRecipe,
  saveRecipe,
  searchItems,
  type Loggable,
  type ResolvedIngredient,
  type ResolvedRecipe,
} from "@/lib/api"
import {
  useBackgroundJobs,
  useDebouncedEffect,
  useDraft,
} from "@/lib/backgroundWork"
import { formatDateRelative, localToday, localYesterday } from "@/lib/dates"

/**
 * MealComposer — single surface for logging a meal.
 *
 * Two modes, equal citizens:
 *  - **Describe** (default): free-form text → Claude parses → editable per-item
 *    review → saves as an auto-named recipe + logs one meal of total_yield_g.
 *  - **Pick item**: search ingredients/recipes by name → quantity → log one row.
 *    (This is the prior QuickMealLog flow, kept as a first-class affordance.)
 *
 * Polling discipline (the original "stuck" bug):
 *  - explicit STAGES (reading → matching foods → computing nutrition → done)
 *  - elapsed counter, visible from second 1
 *  - cancel button always present
 *  - soft warning at 30s, hard timeout at 90s with "retry" affordance
 *  - AbortController per request + jobIdRef so stale resolutions are ignored
 */

type Mode = "describe" | "pick"
type MealType = "breakfast" | "lunch" | "dinner" | "snack"

const SOFT_TIMEOUT_MS = 30_000
const HARD_TIMEOUT_MS = 90_000
// Minimum visual duration per stage. The provider may flip the job to
// "succeeded" in <1s for short inputs, so the stage indicator times
// itself off elapsed-since-createdAt to walk all three before the
// review panel takes over.
const MIN_STAGE_DISPLAY_MS = 900

// The page that hosts the composer — used as the originPath stamped on
// any tracked background parse jobs, so clicking the global pill returns
// the user here. Hardcoded today; if the composer ever embeds elsewhere
// we'll lift this to a prop.
const COMPOSER_ORIGIN = "/today"

export function MealComposer({ onLogged }: { onLogged: (loggedDate: string) => void }) {
  const [mode, setMode] = useState<Mode>("describe")
  // mealType + logDate are drafted so a tab-switch mid-edit doesn't
  // lose them. Short TTL via the hook's default (1h) — long enough for
  // the typical work-and-come-back flow, not long enough to surprise
  // someone next morning.
  const [mealType, setMealType] = useDraft<MealType>(
    "composer:mealType",
    suggestMealType(),
  )
  // The date the meal is logged FOR (not the wall-clock now). Defaults to
  // today; backfill flows pick yesterday or earlier.
  const [logDate, setLogDate] = useDraft<string>(
    "composer:logDate",
    localToday(),
  )

  const handleLogged = () => onLogged(logDate)

  return (
    <div className="card-surface px-6 sm:px-8 py-7">
      <div className="flex items-baseline justify-between mb-4">
        <p className="eyebrow">log a meal</p>
        <ModeToggle value={mode} onChange={setMode} />
      </div>

      <DateSelector value={logDate} onChange={setLogDate} />

      {mode === "describe" ? (
        <DescribeMode
          mealType={mealType}
          setMealType={setMealType}
          logDate={logDate}
          onLogged={handleLogged}
          onSwitchToPick={() => setMode("pick")}
        />
      ) : (
        <PickMode
          mealType={mealType}
          setMealType={setMealType}
          logDate={logDate}
          onLogged={handleLogged}
        />
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   DATE SELECTOR — today / yesterday / pick a day
// ════════════════════════════════════════════════════════════════════════════

function DateSelector({
  value,
  onChange,
}: {
  value: string
  onChange: (ymd: string) => void
}) {
  const today = localToday()
  const yesterday = localYesterday()
  const isToday = value === today
  const isYesterday = value === yesterday
  const isOther = !isToday && !isYesterday

  return (
    <div className="mb-6 flex flex-wrap items-baseline gap-x-3 gap-y-2">
      <span className="eyebrow text-muted-foreground">logging for</span>
      <div className="flex flex-wrap items-baseline gap-1">
        <DateChip active={isToday} onClick={() => onChange(today)}>
          today
        </DateChip>
        <DateChip active={isYesterday} onClick={() => onChange(yesterday)}>
          yesterday
        </DateChip>
        <label
          className={`relative inline-flex items-baseline rounded font-sans text-xs tracking-tight transition-colors cursor-pointer ${
            isOther
              ? "bg-foreground text-background"
              : "bg-surface border border-border text-muted-foreground hover:text-foreground"
          }`}
        >
          <span className="px-3 py-1.5">
            {isOther ? formatDateRelative(value) : "earlier ▾"}
          </span>
          <input
            type="date"
            value={value}
            max={today}
            onChange={(e) => {
              if (e.target.value) onChange(e.target.value)
            }}
            // Overlay an invisible native picker so the chip is the tap target
            // but the OS-native date UI handles selection. Past-only via `max`.
            className="absolute inset-0 opacity-0 cursor-pointer"
            aria-label="Pick an earlier date"
          />
        </label>
      </div>
      {isOther && (
        <button
          type="button"
          onClick={() => onChange(today)}
          className="ml-auto eyebrow text-muted-foreground hover:text-foreground transition-colors"
        >
          ↻ back to today
        </button>
      )}
    </div>
  )
}

function DateChip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded font-sans text-xs tracking-tight transition-colors ${
        active
          ? "bg-foreground text-background"
          : "bg-surface border border-border text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   DESCRIBE MODE — narrative input → parse → review → log
// ════════════════════════════════════════════════════════════════════════════

function DescribeMode({
  mealType,
  setMealType,
  logDate,
  onLogged,
  onSwitchToPick,
}: {
  mealType: MealType
  setMealType: (m: MealType) => void
  logDate: string
  onLogged: () => void
  onSwitchToPick: () => void
}) {
  // Idle-phase typing survives navigation via useDraft. Cleared once the
  // user submits a parse OR explicitly discards.
  const [input, setInput, clearInputDraft] = useDraft<string>(
    "composer:describeText",
    "",
  )

  const { jobForOrigin, trackParseJob, dismissJob } = useBackgroundJobs()
  const job = jobForOrigin(COMPOSER_ORIGIN)

  // Local "we're talking to the server to enqueue" state. Sub-second; we
  // never need to persist it. Once the server returns a job_id, the
  // provider takes over.
  const [enqueueErr, setEnqueueErr] = useState<string | null>(null)
  const submittingRef = useRef(false)

  // Clear the enqueue error as soon as the user starts editing again —
  // otherwise a stale "server unavailable" sticks around until the next
  // submit, which feels broken if the user has already adjusted the
  // input.
  useEffect(() => {
    if (enqueueErr) setEnqueueErr(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input])

  async function onParse() {
    const text = input.trim()
    if (text.length < 3 || submittingRef.current) return
    submittingRef.current = true
    setEnqueueErr(null)
    try {
      const j = await parseRecipe("text", text)
      // Drop into the provider — polling continues across navigations.
      trackParseJob(
        j.job_id,
        // Label kept short for the pill; the parsing panel echoes the
        // full text from the input draft, not from here.
        text.length > 60 ? text.slice(0, 57).trimEnd() + "…" : text,
        COMPOSER_ORIGIN,
      )
    } catch (err: any) {
      setEnqueueErr(
        err?.response?.data?.error ||
          err?.message ||
          "Couldn't kick off the parser. Try again?",
      )
    } finally {
      submittingRef.current = false
    }
  }

  // Full reset: drop tracked job + clear the draft. Used by both cancel
  // and successful-log paths.
  function resetAll() {
    if (job) dismissJob(job.id)
    clearInputDraft()
    setEnqueueErr(null)
  }

  // ─── PARSING (provider says pending/running): stages + elapsed + cancel
  if (job && (job.status === "pending" || job.status === "running")) {
    return (
      <ParsingPanel
        job={job}
        input={input || job.label}
        onCancel={resetAll}
      />
    )
  }

  // ─── REVIEW (provider says succeeded): editable per-item, then log
  if (job && job.status === "succeeded" && job.result) {
    return (
      <ReviewPanelHost
        jobId={job.id}
        result={job.result}
        edits={job.reviewEdits}
        input={input || job.label}
        mealType={mealType}
        setMealType={setMealType}
        logDate={logDate}
        onLogged={() => {
          resetAll()
          onLogged()
        }}
        onCancel={resetAll}
      />
    )
  }

  // ─── FAILED / EXPIRED: friendly retry
  if (job && (job.status === "failed" || job.status === "expired")) {
    const lastText = input || job.label
    return (
      <div className="space-y-5">
        <div className="px-4 py-3 border-l-2 border-persimmon bg-persimmon/[0.04]">
          <p className="eyebrow text-persimmon mb-1">
            {job.status === "expired" ? "parse expired" : "parse failed"}
          </p>
          <p className="font-sans text-sm">
            {job.error ||
              (job.status === "expired"
                ? "The parser took too long and the job was reaped. Try again?"
                : "The parser couldn't make sense of that. Try rephrasing — e.g. quantities like 'two slices' or '100g' help.")}
          </p>
        </div>
        <textarea
          value={lastText}
          onChange={(e) => setInput(e.target.value)}
          rows={3}
          className="w-full bg-transparent border-0 border-b border-border focus:border-moss focus:outline-none px-0 py-2 font-sans text-base resize-y"
        />
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => {
              dismissJob(job.id)
              // Fire next tick so the dismiss settles into state before
              // we kick off a new parse against the same input.
              setTimeout(onParse, 0)
            }}
            className="bg-foreground text-background px-5 py-2.5 rounded font-sans text-sm hover:bg-moss transition-colors"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={resetAll}
            className="eyebrow text-muted-foreground hover:text-foreground transition-colors"
          >
            discard
          </button>
        </div>
      </div>
    )
  }

  // ─── IDLE: textarea + parse button (default state — no tracked job) ─────
  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <label
          htmlFor="composer-input"
          className="eyebrow block text-muted-foreground"
        >
          describe your meal
        </label>
        <textarea
          id="composer-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") onParse()
          }}
          placeholder='e.g. "two-egg omelette with onions, sourdough toast, half an avocado"'
          rows={3}
          className="w-full bg-transparent border-0 border-b border-border focus:border-moss focus:outline-none px-0 py-3 font-sans text-base leading-relaxed resize-y placeholder:italic placeholder:text-muted-foreground/70"
        />
        <p className="font-mono text-[11px] text-muted-foreground">
          free-form. quantities can be loose ("half an avocado") — we'll
          resolve them into grams.
        </p>
      </div>

      <MealTypePicker value={mealType} onChange={setMealType} />

      {enqueueErr && (
        <p className="font-sans text-sm text-persimmon">{enqueueErr}</p>
      )}

      <div className="flex flex-wrap items-center gap-x-5 gap-y-3 pt-1">
        <button
          type="button"
          onClick={onParse}
          disabled={input.trim().length < 3}
          className="bg-foreground text-background px-6 py-3 rounded font-sans text-sm tracking-tight hover:bg-moss transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Parse meal →
        </button>
        <span className="font-mono text-[11px] text-muted-foreground">
          ⌘↵ to parse
        </span>
        <button
          type="button"
          onClick={onSwitchToPick}
          className="ml-auto eyebrow text-muted-foreground hover:text-foreground transition-colors"
        >
          or pick a single item ↓
        </button>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   PARSING PANEL — driven by the BackgroundWorkProvider
//
//   Polling no longer happens here; the provider owns it cross-page. This
//   panel is now purely visual: stage indicator, elapsed counter, cancel.
//   When the provider flips the job to succeeded/failed, DescribeMode
//   swaps to the review/error branch on the next render.
// ════════════════════════════════════════════════════════════════════════════

function ParsingPanel({
  job,
  input,
  onCancel,
}: {
  job: { id: string; createdAt: number; status: string }
  input: string
  onCancel: () => void
}) {
  const [elapsed, setElapsed] = useState(Date.now() - job.createdAt)

  // Tick the elapsed counter. createdAt comes from the provider — survives
  // page navigations, so a parse you kicked off 8s ago and came back to
  // shows "8.0s" instantly, not "0.0s".
  useEffect(() => {
    const id = setInterval(
      () => setElapsed(Date.now() - job.createdAt),
      250,
    )
    return () => clearInterval(id)
  }, [job.createdAt])

  // Soft "still working" warning past 30s. Hard timeout (90s) used to
  // synthesize a failure here; now the provider keeps polling and the
  // server-side reaper (5min) handles truly stuck jobs. We surface the
  // soft warning so the user knows it's not frozen.
  const stageIndex = stageForElapsed(elapsed)
  const softTimedOut = elapsed > SOFT_TIMEOUT_MS
  const hardTimedOut = elapsed > HARD_TIMEOUT_MS

  function handleCancel() {
    onCancel()
  }

  return (
    <div className="space-y-5 animate-fade-up">
      {/* Echo of input — past-tense italicized */}
      <blockquote className="border-l-2 border-moss/40 pl-4 italic font-sans text-sm text-muted-foreground leading-relaxed">
        "{input}"
      </blockquote>

      {/* Stages indicator */}
      <div className="space-y-3">
        <div className="flex items-center gap-4">
          {STAGES.map((label, i) => {
            const active = i === stageIndex
            const done = i < stageIndex
            return (
              <div key={label} className="flex items-center gap-2 min-w-0">
                <span
                  aria-hidden
                  className={`block w-2 h-2 rounded-full transition-colors duration-500 ${
                    done
                      ? "bg-moss"
                      : active
                      ? "bg-foreground animate-pulse"
                      : "bg-border"
                  }`}
                />
                <span
                  className={`font-mono text-[11px] uppercase tracking-wider whitespace-nowrap transition-colors duration-500 ${
                    active
                      ? "text-foreground"
                      : done
                      ? "text-moss"
                      : "text-muted-foreground/60"
                  }`}
                >
                  {label}
                </span>
                {i < STAGES.length - 1 && (
                  <span
                    aria-hidden
                    className={`block h-px w-6 sm:w-10 transition-colors duration-500 ${
                      done ? "bg-moss" : "bg-border"
                    }`}
                  />
                )}
              </div>
            )
          })}
        </div>

        {/* Shimmer progress + elapsed */}
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px bg-border relative overflow-hidden">
            <div
              className="absolute top-0 left-0 h-px bg-moss"
              style={{
                width: `${Math.min(95, (elapsed / HARD_TIMEOUT_MS) * 100)}%`,
                transition: "width 250ms linear",
              }}
            />
            <div
              className="absolute top-0 left-0 h-px bg-foreground/20 animate-[shimmer_2s_ease-in-out_infinite]"
              style={{ width: "30%" }}
            />
          </div>
          <span className="font-mono text-[11px] text-muted-foreground tabular-nums whitespace-nowrap">
            {(elapsed / 1000).toFixed(1)}s
          </span>
          <button
            type="button"
            onClick={handleCancel}
            className="eyebrow text-muted-foreground hover:text-persimmon transition-colors"
          >
            cancel
          </button>
        </div>

        {(softTimedOut || hardTimedOut) && (
          <p className="font-sans text-xs text-muted-foreground italic">
            {hardTimedOut
              ? "Still going — the server keeps trying for up to 5 min. You can leave this page; we'll keep parsing in the background."
              : "Still working — Claude can take up to a minute on long inputs. Hang tight or cancel to retry."}
          </p>
        )}
      </div>
    </div>
  )
}

const STAGES = ["reading", "matching foods", "computing nutrition"] as const

// Tight buckets so fast parses still walk all three stages. Slow parses
// (USDA cache miss + Claude fallback) naturally linger on stage 2 — that's
// correct.
function stageForElapsed(ms: number): number {
  if (ms < MIN_STAGE_DISPLAY_MS) return 0
  if (ms < MIN_STAGE_DISPLAY_MS * 2) return 1
  return 2
}

// ════════════════════════════════════════════════════════════════════════════
//   REVIEW PANEL HOST — owns the ephemeral edit state
//
//   The provider holds the immutable parser result. Edits the user makes
//   here (per-ingredient grams, name override) live in local state — if
//   the user navigates away mid-review their tweaks reset, but the parse
//   itself is preserved. Acceptable v1 tradeoff; can persist edits later
//   if the loss-of-edits friction shows up in practice.
// ════════════════════════════════════════════════════════════════════════════

function ReviewPanelHost({
  jobId,
  result,
  edits,
  input,
  mealType,
  setMealType,
  logDate,
  onLogged,
  onCancel,
}: {
  jobId: string
  result: ResolvedRecipe
  edits?: { ingredients?: ResolvedIngredient[]; nameOverride?: string }
  input: string
  mealType: MealType
  setMealType: (m: MealType) => void
  logDate: string
  onLogged: () => void
  onCancel: () => void
}) {
  const { updateJobEdits } = useBackgroundJobs()

  // Seed local state from the job's persisted edits if present, else
  // from the raw parse result. Local-state-as-cache means typing in the
  // name field doesn't bounce through the provider on every keystroke;
  // we sync on blur / changes via the effects below.
  const [draft, setDraft] = useState<ResolvedRecipe>(() => {
    if (edits?.ingredients) {
      return { ...result, ingredients: edits.ingredients }
    }
    return result
  })
  const [nameOverride, setNameOverride] = useState<string>(
    () => edits?.nameOverride ?? deriveAutoName(input, mealType),
  )

  // Persist edits back to the provider. Debounced 300ms so typing in
  // the name field or scrubbing a quantity input doesn't trigger a
  // localStorage write per keystroke (which used to thrash on slower
  // devices). Flush-on-unmount preserves the last burst even if the
  // user navigates within the debounce window.
  useDebouncedEffect(
    () => {
      updateJobEdits(jobId, {
        ingredients: draft.ingredients,
        nameOverride,
      })
    },
    [jobId, draft.ingredients, nameOverride],
    300,
  )

  return (
    <ReviewPanel
      draft={draft}
      nameOverride={nameOverride}
      mealType={mealType}
      setMealType={setMealType}
      logDate={logDate}
      onUpdateName={setNameOverride}
      onUpdateDraft={setDraft}
      onLogged={onLogged}
      onCancel={onCancel}
    />
  )
}

function ReviewPanel({
  draft,
  nameOverride,
  mealType,
  setMealType,
  logDate,
  onUpdateName,
  onUpdateDraft,
  onLogged,
  onCancel,
}: {
  draft: ResolvedRecipe
  nameOverride: string
  mealType: MealType
  setMealType: (m: MealType) => void
  logDate: string
  onUpdateName: (name: string) => void
  onUpdateDraft: (d: ResolvedRecipe) => void
  onLogged: () => void
  onCancel: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const busyRef = useRef(false)

  // Recompute totals + per-100g from the current ingredient quantities.
  const totals = computeTotals(draft.ingredients)
  const totalYieldG = draft.ingredients.reduce((a, i) => a + i.quantity_g, 0)

  function updateQty(idx: number, qty: number) {
    const next = { ...draft, ingredients: [...draft.ingredients] }
    next.ingredients[idx] = { ...next.ingredients[idx], quantity_g: qty }
    onUpdateDraft(next)
  }
  function removeItem(idx: number) {
    const next = { ...draft, ingredients: draft.ingredients.filter((_, i) => i !== idx) }
    onUpdateDraft(next)
  }

  // origin="log" → composed-meal row hidden from the recipe library; still
  //                searchable in the meal-log picker. The common case: one-off
  //                meals you don't intend to reuse by name.
  // origin="saved" → first-class entry in the recipe library, alongside
  //                  things you created on /recipes/new.
  async function commit(origin: "log" | "saved") {
    if (busyRef.current) return
    if (totalYieldG <= 0) {
      setErr("Add at least one item with grams > 0.")
      return
    }
    busyRef.current = true
    setBusy(true)
    setErr(null)
    try {
      const recipe: ResolvedRecipe = {
        ...draft,
        name: nameOverride.trim() || "Meal",
        total_yield_g: totalYieldG,
        servings: 1,
        per_100g: {
          calories: totalYieldG > 0 ? (totals.calories / totalYieldG) * 100 : 0,
          protein_g: totalYieldG > 0 ? (totals.protein_g / totalYieldG) * 100 : 0,
          fiber_g: totalYieldG > 0 ? (totals.fiber_g / totalYieldG) * 100 : 0,
          carbs_g: totalYieldG > 0 ? (totals.carbs_g / totalYieldG) * 100 : 0,
          sugar_g: totalYieldG > 0 ? (totals.sugar_g / totalYieldG) * 100 : 0,
        },
        totals,
      }
      const saved = await saveRecipe(recipe, origin)
      await createMeal({
        date: logDate, // composer-selected local date (today / yesterday / earlier)
        meal_type: mealType,
        recipe_id: saved.id,
        quantity_g: totalYieldG,
      })
      onLogged()
    } catch (e: any) {
      setErr(
        e?.response?.data?.error ||
          e?.message ||
          "Couldn't log the meal. Try again?"
      )
      busyRef.current = false
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-6 animate-fade-up">
      {/* Eyebrow row: badge + totals */}
      <div className="flex items-baseline justify-between gap-4">
        <p className="eyebrow">
          resolved · {draft.ingredients.length}{" "}
          {draft.ingredients.length === 1 ? "item" : "items"}
        </p>
        <p className="font-mono text-[11px] text-muted-foreground tabular-nums">
          confidence · {draft.confidence}
        </p>
      </div>

      {/* Editable name */}
      <div>
        <label className="eyebrow block mb-2">name this meal</label>
        <input
          type="text"
          value={nameOverride}
          onChange={(e) => onUpdateName(e.target.value)}
          className="w-full bg-transparent border-0 border-b border-border focus:border-moss focus:outline-none px-0 py-2 font-display text-2xl tracking-tight"
          placeholder="Sunday breakfast"
        />
      </div>

      {/* Items */}
      <ul className="border-t border-b border-border divide-y divide-border">
        {draft.ingredients.map((ing, i) => (
          <li
            key={`${ing.name}-${i}`}
            className="py-4 grid grid-cols-[1fr_auto_auto] items-baseline gap-4 animate-fade-up"
            style={{ animationDelay: `${i * 40}ms` }}
          >
            <div className="min-w-0">
              <p className="font-sans text-base text-foreground truncate">
                {ing.name}
              </p>
              <p className="font-mono text-[11px] text-muted-foreground">
                {sourceBadge(ing.source)} ·{" "}
                {ing.calories_per_100g.toFixed(0)} kcal /100g
              </p>
            </div>
            <div className="flex items-baseline gap-1.5 border-b border-border focus-within:border-moss transition-colors">
              <input
                type="number"
                value={ing.quantity_g}
                onChange={(e) =>
                  updateQty(i, parseFloat(e.target.value) || 0)
                }
                step={5}
                min={0}
                className="w-20 bg-transparent border-0 outline-none font-display text-xl tracking-tight num text-right"
              />
              <span className="font-mono text-[10px] text-muted-foreground">
                g
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-sm text-foreground tabular-nums whitespace-nowrap min-w-[4rem] text-right">
                {((ing.calories_per_100g * ing.quantity_g) / 100).toFixed(0)} kcal
              </span>
              <button
                type="button"
                onClick={() => removeItem(i)}
                className="eyebrow text-muted-foreground hover:text-persimmon transition-colors"
                aria-label={`Remove ${ing.name}`}
                title="Remove"
              >
                ✕
              </button>
            </div>
          </li>
        ))}
      </ul>

      {/* Total */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
        <TotalCell label="calories" value={totals.calories} unit="kcal" emphasized />
        <TotalCell label="protein" value={totals.protein_g} unit="g" />
        <TotalCell label="fiber" value={totals.fiber_g} unit="g" />
        <TotalCell label="carbs" value={totals.carbs_g} unit="g" />
      </div>

      <MealTypePicker value={mealType} onChange={setMealType} />

      {err && <p className="font-sans text-sm text-persimmon">{err}</p>}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <button
          type="button"
          onClick={() => commit("log")}
          disabled={busy || totalYieldG <= 0}
          className="bg-foreground text-background px-6 py-3 rounded font-sans text-sm tracking-tight hover:bg-moss transition-colors disabled:opacity-50"
          title="Log this once. Won't appear in your recipe library."
        >
          {busy ? "Logging…" : "Log once"}
        </button>
        <button
          type="button"
          onClick={() => commit("saved")}
          disabled={busy || totalYieldG <= 0}
          className="border border-foreground/30 text-foreground px-5 py-3 rounded font-sans text-sm tracking-tight hover:border-moss hover:text-moss transition-colors disabled:opacity-50"
          title="Save in your recipe library so you can re-pick it by name."
        >
          {busy ? "…" : "Save & log"}
        </button>
        <p className="font-mono text-[11px] text-muted-foreground basis-full sm:basis-auto leading-relaxed">
          <span className="text-foreground">Log once</span> = today's log only.{" "}
          <span className="text-foreground">Save &amp; log</span> = also keeps
          a named copy in <span className="italic">recipes</span> for later.
        </p>
        <button
          type="button"
          onClick={onCancel}
          className="eyebrow text-muted-foreground hover:text-persimmon transition-colors ml-auto"
        >
          discard
        </button>
      </div>
    </div>
  )
}

function TotalCell({
  label,
  value,
  unit,
  emphasized = false,
}: {
  label: string
  value: number
  unit: string
  emphasized?: boolean
}) {
  const size = emphasized ? "text-3xl sm:text-4xl" : "text-2xl"
  return (
    <div>
      <p className="eyebrow mb-1">{label}</p>
      <div className="flex items-baseline gap-1.5">
        <span className={`font-display ${size} tracking-tightest num`}>
          {value.toFixed(0)}
        </span>
        <span className="font-mono text-[10px] text-muted-foreground">
          {unit}
        </span>
      </div>
    </div>
  )
}

function computeTotals(items: ResolvedIngredient[]) {
  const t = { calories: 0, protein_g: 0, fiber_g: 0, carbs_g: 0, sugar_g: 0 }
  for (const i of items) {
    const f = i.quantity_g / 100
    t.calories += i.calories_per_100g * f
    t.protein_g += i.protein_per_100g * f
    t.fiber_g += i.fiber_per_100g * f
    t.carbs_g += i.carbs_per_100g * f
    t.sugar_g += i.sugar_per_100g * f
  }
  return t
}

function sourceBadge(source: "usda" | "claude" | "user"): string {
  if (source === "usda") return "USDA"
  if (source === "claude") return "estimated"
  return "your library"
}

function deriveAutoName(input: string, mealType: MealType): string {
  // Keep the user's natural description as the name — long names display
  // and wrap fine on the recipe-detail page, and a descriptive name is
  // far more useful for re-picking later than "Sourdough bread, half a…".
  // Cap at 120 chars (safe column-width bound on the DB side) and trim
  // any trailing whitespace.
  const clean = input.replace(/\s+/g, " ").trim()
  if (clean.length >= 4) {
    const capped = clean.length > 120 ? clean.slice(0, 117).trimEnd() + "…" : clean
    return capped[0].toUpperCase() + capped.slice(1)
  }
  const t = new Date()
  const time = t.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
  return `${mealType[0].toUpperCase()}${mealType.slice(1)} · ${time}`
}

// ════════════════════════════════════════════════════════════════════════════
//   PICK MODE — preserved from the original QuickMealLog
// ════════════════════════════════════════════════════════════════════════════

function PickMode({
  mealType,
  setMealType,
  logDate,
  onLogged,
}: {
  mealType: MealType
  setMealType: (m: MealType) => void
  logDate: string
  onLogged: () => void
}) {
  // PickMode state survives navigation via useDraft. Same TTL contract as
  // DescribeMode (1h). The picked item is a JSON-serializable Loggable;
  // if the underlying ingredient is later deleted from the library, the
  // server call on submit will 404 and the user retries.
  const [query, setQuery] = useDraft<string>("composer:pickQuery", "")
  const [picked, setPicked] = useDraft<Loggable | null>(
    "composer:pickPicked",
    null,
  )
  // Quantity model. Pure-grams entry (`unitMode==="grams"`) or
  // count × grams-per-unit entry (`unitMode==="piece"`). The server
  // contract is always grams; piece mode is purely a typing aid.
  const [unitMode, setUnitMode] = useDraft<"grams" | "piece">(
    "composer:pickUnitMode",
    "grams",
  )
  const [grams, setGrams] = useDraft<number>("composer:pickGrams", 100)
  const [count, setCount] = useDraft<number>("composer:pickCount", 1)
  const [gramsPerUnit, setGramsPerUnit] = useDraft<number>(
    "composer:pickGramsPerUnit",
    50,
  )
  // Ephemeral state — typeahead results and transient submit signals.
  const [results, setResults] = useState<Loggable[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const busyRef = useRef(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Total grams (what the server actually receives).
  const qty =
    unitMode === "piece"
      ? Math.max(0, count) * Math.max(0, gramsPerUnit)
      : Math.max(0, grams)

  // When a NEW item is picked, restore its cached g/unit (if any) and
  // default to piece mode — most people who reach for a known item think
  // in pieces ("2 eggs"), not grams.
  //
  // Critical: we skip on initial mount when `picked` came from the
  // persisted draft. Otherwise rehydrating after a tab switch would
  // clobber the user's in-progress quantity edits (regression from the
  // useDraft conversion). prevPicked starts equal to the current picked
  // so the first effect run sees no change.
  const prevPicked = useRef<Loggable | null>(picked)
  useEffect(() => {
    if (!picked) {
      prevPicked.current = null
      return
    }
    if (prevPicked.current?.id === picked.id) return
    prevPicked.current = picked
    const cached = readUnitCache(picked.id)
    if (cached) {
      setGramsPerUnit(cached)
      setCount(1)
      setUnitMode("piece")
    } else {
      setUnitMode("grams")
      setGrams(100)
      setCount(1)
      setGramsPerUnit(50)
    }
  }, [picked, setGramsPerUnit, setCount, setUnitMode, setGrams])

  useEffect(() => {
    if (picked || query.trim().length < 2) {
      setResults([])
      return
    }
    const ctrl = new AbortController()
    const handle = setTimeout(async () => {
      try {
        const r = await searchItems(query)
        if (!ctrl.signal.aborted) setResults(r)
      } catch {
        if (!ctrl.signal.aborted) setResults([])
      }
    }, 200)
    return () => {
      clearTimeout(handle)
      ctrl.abort()
    }
  }, [query, picked])

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (busyRef.current) return
    if (!picked) {
      setError("Pick an item first.")
      return
    }
    if (qty <= 0) {
      setError("Quantity must be > 0.")
      return
    }
    busyRef.current = true
    setBusy(true)
    setError(null)
    try {
      await createMeal({
        date: logDate, // composer-selected local date (today / yesterday / earlier)
        meal_type: mealType,
        ...(picked.kind === "recipe"
          ? { recipe_id: picked.id }
          : { ingredient_id: picked.id }),
        quantity_g: qty,
      })
      if (unitMode === "piece" && gramsPerUnit > 0) {
        writeUnitCache(picked.id, gramsPerUnit)
      }
      setPicked(null)
      setQuery("")
      setGrams(100)
      setCount(1)
      setUnitMode("grams")
      setResults([])
      onLogged()
      inputRef.current?.focus()
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Save failed.")
    } finally {
      setBusy(false)
      busyRef.current = false
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      {picked ? (
        <div className="flex items-center justify-between gap-4 border border-moss/40 bg-moss/[0.04] px-4 py-3 rounded">
          <div className="min-w-0">
            <p className="font-sans text-base text-foreground truncate">
              {picked.kind === "recipe" && (
                <span className="font-mono text-xs text-moss mr-2">RECIPE</span>
              )}
              {picked.name}
            </p>
            <p className="font-mono text-xs text-muted-foreground">
              {picked.calories_per_100g.toFixed(0)} kcal ·{" "}
              {picked.protein_per_100g.toFixed(1)}g protein per 100g
              {picked.kind === "ingredient" &&
                (picked.shared ? " · shared" : " · yours")}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setPicked(null)
              setTimeout(() => inputRef.current?.focus(), 0)
            }}
            className="eyebrow hover:text-foreground transition-colors"
          >
            change
          </button>
        </div>
      ) : (
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search ingredients (chickpeas, oats, salmon…)"
            className="w-full bg-transparent border-0 border-b border-border focus:border-moss focus:outline-none px-0 py-3 font-sans text-base"
            autoComplete="off"
          />
          {results.length > 0 && (
            <ul className="absolute z-10 mt-1 w-full bg-surface border border-border rounded shadow-sm max-h-72 overflow-y-auto">
              {results.map((r) => (
                <li key={`${r.kind}-${r.id}`}>
                  <button
                    type="button"
                    onClick={() => {
                      setPicked(r)
                      setQuery("")
                      setResults([])
                    }}
                    className="w-full text-left px-4 py-2.5 hover:bg-muted transition-colors"
                  >
                    <p className="font-sans text-sm text-foreground flex items-center gap-2">
                      {r.kind === "recipe" && (
                        <span className="font-mono text-[10px] text-moss border border-moss/40 rounded px-1.5 uppercase tracking-smallcaps">
                          recipe
                        </span>
                      )}
                      {r.name}
                    </p>
                    <p className="font-mono text-xs text-muted-foreground">
                      {r.calories_per_100g.toFixed(0)} kcal / 100g
                      {r.kind === "ingredient" &&
                        (r.shared ? " · shared" : " · yours")}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="space-y-5">
        <div>
          <div className="flex items-baseline justify-between gap-3 mb-2">
            <span className="eyebrow">quantity</span>
            <div
              role="tablist"
              aria-label="Quantity unit"
              className="inline-flex border border-border rounded overflow-hidden text-[10px]"
            >
              {(
                [
                  { id: "grams" as const, label: "g" },
                  { id: "piece" as const, label: "by piece" },
                ]
              ).map((opt) => {
                const active = unitMode === opt.id
                return (
                  <button
                    key={opt.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setUnitMode(opt.id)}
                    className={`px-2.5 py-1 font-mono uppercase tracking-wider transition-colors ${
                      active
                        ? "bg-foreground text-background"
                        : "bg-surface text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>
          </div>

          {unitMode === "grams" ? (
            <div className="flex items-baseline gap-2 border-b border-border focus-within:border-moss transition-colors py-1.5">
              <input
                type="number"
                value={Number.isFinite(grams) ? grams : ""}
                onChange={(e) => setGrams(parseFloat(e.target.value) || 0)}
                step={5}
                min={0}
                inputMode="decimal"
                className="flex-1 min-w-0 bg-transparent border-0 outline-none font-display text-2xl tracking-tight num text-foreground"
              />
              <span className="font-mono text-xs text-muted-foreground">g</span>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Two number inputs — stacked on mobile (one per row),
                  inline on sm+ with a "×" glyph between them. The display
                  font scales down on mobile so two inputs + labels never
                  overflow even on a 320px viewport. */}
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-x-3 gap-y-2 sm:items-baseline">
                <label className="flex items-baseline gap-2 border-b border-border focus-within:border-moss transition-colors py-1.5">
                  <input
                    type="number"
                    value={Number.isFinite(count) ? count : ""}
                    onChange={(e) => setCount(parseFloat(e.target.value) || 0)}
                    step={1}
                    min={0}
                    inputMode="decimal"
                    aria-label="Count"
                    className="flex-1 min-w-0 bg-transparent border-0 outline-none font-display text-xl sm:text-2xl tracking-tight num text-foreground"
                  />
                  <span className="font-mono text-[10px] text-muted-foreground whitespace-nowrap">
                    pieces
                  </span>
                </label>
                <span
                  aria-hidden
                  className="hidden sm:inline self-center font-mono text-sm text-muted-foreground pb-0.5"
                >
                  ×
                </span>
                <label className="flex items-baseline gap-2 border-b border-border focus-within:border-moss transition-colors py-1.5">
                  <input
                    type="number"
                    value={Number.isFinite(gramsPerUnit) ? gramsPerUnit : ""}
                    onChange={(e) =>
                      setGramsPerUnit(parseFloat(e.target.value) || 0)
                    }
                    step={5}
                    min={0}
                    inputMode="decimal"
                    aria-label="Grams per piece"
                    className="flex-1 min-w-0 bg-transparent border-0 outline-none font-display text-xl sm:text-2xl tracking-tight num text-foreground"
                  />
                  <span className="font-mono text-[10px] text-muted-foreground whitespace-nowrap">
                    g each
                  </span>
                </label>
              </div>
              <p className="font-mono text-[11px] text-muted-foreground tabular-nums">
                = {qty.toFixed(0)} g total
                {readUnitCache(picked?.id || "") === null && picked && (
                  <span className="ml-2 italic text-muted-foreground/70">
                    (we'll remember for next time)
                  </span>
                )}
              </p>
            </div>
          )}
        </div>

        <MealTypePicker value={mealType} onChange={setMealType} />
      </div>

      {picked && qty > 0 && (
        <div className="font-mono text-xs text-muted-foreground tabular-nums">
          → {((picked.calories_per_100g * qty) / 100).toFixed(0)} kcal ·{" "}
          {((picked.protein_per_100g * qty) / 100).toFixed(1)}g protein ·{" "}
          {((picked.fiber_per_100g * qty) / 100).toFixed(1)}g fiber
        </div>
      )}

      {error && <p className="font-sans text-sm text-persimmon">{error}</p>}

      <button
        type="submit"
        disabled={busy || !picked}
        className="bg-foreground text-background px-6 py-3 rounded font-sans text-sm tracking-tight hover:bg-moss transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {busy ? "Logging…" : "Log meal"}
      </button>
    </form>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   Shared primitives
// ════════════════════════════════════════════════════════════════════════════

function ModeToggle({
  value,
  onChange,
}: {
  value: Mode
  onChange: (m: Mode) => void
}) {
  return (
    <div
      role="tablist"
      aria-label="Meal logging mode"
      className="inline-flex border border-border rounded overflow-hidden text-[11px]"
    >
      {(
        [
          { id: "describe", label: "describe" },
          { id: "pick", label: "pick item" },
        ] as const
      ).map((opt) => {
        const active = value === opt.id
        return (
          <button
            key={opt.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.id)}
            className={`px-3 py-1 font-mono uppercase tracking-wider transition-colors ${
              active
                ? "bg-foreground text-background"
                : "bg-surface text-muted-foreground hover:text-foreground"
            }`}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

function MealTypePicker({
  value,
  onChange,
}: {
  value: MealType
  onChange: (m: MealType) => void
}) {
  return (
    <div>
      <span className="eyebrow block mb-2">meal</span>
      <div className="flex gap-1 flex-wrap">
        {(["breakfast", "lunch", "dinner", "snack"] as const).map((mt) => {
          const active = value === mt
          return (
            <button
              key={mt}
              type="button"
              onClick={() => onChange(mt)}
              className={`px-3 py-1.5 rounded font-sans text-xs tracking-tight transition-colors ${
                active
                  ? "bg-foreground text-background"
                  : "bg-surface border border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {mt}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function suggestMealType(): MealType {
  const h = new Date().getHours()
  if (h < 10) return "breakfast"
  if (h < 14) return "lunch"
  if (h < 21) return "dinner"
  return "snack"
}

// ════════════════════════════════════════════════════════════════════════════
//   Unit cache — per-item grams-per-unit, in localStorage
//
//   Users naturally think in pieces ("2 eggs", "1 banana", "3 slices") rather
//   than grams. Caching the chosen grams-per-unit per item id means the
//   second time the same item is picked the composer defaults to piece mode
//   with the right conversion — they just type a count.
// ════════════════════════════════════════════════════════════════════════════

const UNIT_CACHE_PREFIX = "fittrack:gpu:"

function readUnitCache(itemId: string): number | null {
  if (!itemId || typeof window === "undefined") return null
  try {
    const raw = window.localStorage.getItem(UNIT_CACHE_PREFIX + itemId)
    if (!raw) return null
    const n = parseFloat(raw)
    return Number.isFinite(n) && n > 0 ? n : null
  } catch {
    return null
  }
}

function writeUnitCache(itemId: string, gramsPerUnit: number) {
  if (!itemId || typeof window === "undefined") return
  try {
    window.localStorage.setItem(
      UNIT_CACHE_PREFIX + itemId,
      String(gramsPerUnit),
    )
  } catch {
    // Quota exceeded / private mode — degrade silently.
  }
}
